import router from "@/plugins/router";
import {
    useSessionStore,
    useCheckoutStore,
    usePaymentsStore,
} from "@/plugins/store";
import {
    mdiLightningBolt,
    mdiCreditCard,
    mdiWeb,
    mdiBank,
    mdiHelpCircle,
} from "@mdi/js";

import { getPaymentMethodsList } from "@/apis/esendaV2/organisations";
import { getOrderAdditionalInformation } from "@/apis/esendaV2/order";

import { configSentryError } from "@/helpers/sentry";
import { formatAmountBasedOnLocaleAndCurrency } from "@/helpers/amountProcessors";
import { keysToCamelCase } from "@/helpers/stringProcessors";
import { hasValidProperty } from "@/helpers/typeValidations";
import { handleQueryParamsError } from "@/helpers/errorHandling";
import {
    PaymentMethod,
    SelectPaymentMethodItem,
} from "@/helpers/interfaces/organisations";
import {
    BankTransferProviders,
    CardPaymentProviders,
    InstantPaymentProviders,
    PaymentMethods,
    PaymentMethodType,
    SupportedCardType,
    CardBrandNameMap,
} from "@esenda/core-ui/constants";
import { routeNames } from "@/constants/routeNames";
import { useOrganisationStore } from "@/plugins/store";
import { SupportedPremiumCardType } from "@esenda/core-ui/constants";

export const getFirstProvider = (paymentMethods: PaymentMethod[]) => {
    /**
     * Returns the first provider in the payments list based on the
     * lowest displayOrder property value.
     * @param {[PaymentMethod[]]} paymentMethods
     * @returns {[PaymentMethod]}
     */

    return paymentMethods.reduce(function (prev, current) {
        return prev.displayOrder < current.displayOrder ? prev : current;
    });
};

export const getPaymentMethodName = (provider: string) => {
    /**
     * Maps a payment provider to a payment method name.
     * @param {[string]} provider
     * @returns {[string]}
     */
    const paymentMethodsMapping = {
        "Instant Payment": InstantPaymentProviders,
        "Card ": CardPaymentProviders,
        "Bank Transfer": BankTransferProviders,
    };

    return Object.keys(paymentMethodsMapping).find((key) =>
        paymentMethodsMapping[key].includes(provider),
    );
};

export const redirectToPaymentMethod = (paymentMethods: PaymentMethod[]) => {
    const orgStore = useOrganisationStore();

    if (paymentMethods.length == 0) {
        router.push({
            name: routeNames.DISABLED_PAYMENT_METHODS,
            params: {
                shortcode: orgStore.shortcode,
                form: orgStore.formName,
            },
        });
    } else {
        router.push({
            name: routeNames.PAYMENT_METHOD_SELECTION,
            params: {
                shortcode: orgStore.shortcode,
                form: orgStore.formName,
            },
        });
    }
};

export const getPaymentMethodCategory = (provider: string | null) => {
    /**
     * Maps a payment provider to a payment method category.
     * @param {[string]} provider
     * @returns {[string]}
     */

    if (provider === null) {
        return "";
    }
    const paymentMethodsMapping = {
        instant: InstantPaymentProviders,
        card: CardPaymentProviders,
        transfer: BankTransferProviders,
    };

    return Object.keys(paymentMethodsMapping).find((key) =>
        paymentMethodsMapping[key].includes(provider),
    );
};

/**
 * Converts the list of payment methods in a format accepted by Vuetify's
 * custom selector component. It formats the amount based on the currency
 * and the user's locale.
 * @returns {[value: string, title: string | undefined, props: { amount: string, availability: PaymentAvailability }][]}
 *
 * @example
 * {
 *  value: "truelayer",
 *  title: "Instant Payment",
 *  props: {
 *      amount: "£123.12"
 *    }
 * }
 */
export const getPaymentMethodsSelectorList = () => {
    const paymentStore = usePaymentsStore();
    const sessionStore = useSessionStore();
    const locale = sessionStore.language ?? "en-GB";
    const paymentMethods = paymentStore.paymentMethods;

    const formattedPaymentMethodsList: SelectPaymentMethodItem[] = [];
    if (paymentMethods !== null) {
        for (const method of paymentMethods) {
            const amount = formatAmountBasedOnLocaleAndCurrency(
                locale,
                method.localCurrencyCode,
                method.localAmount,
            );
            formattedPaymentMethodsList.push({
                value: method.paymentMethod,
                title: getPaymentMethodName(method.paymentMethod),
                props: {
                    amount: amount,
                    availability: method.availability,
                    limitMax: method.limitMax,
                },
            });
        }
    }
    return formattedPaymentMethodsList;
};

/**
 * Uses the selected payment method to return the formatted
 * or unformatted amount based on the user's locale and currency.
 * @param {[boolean]} formatAmount - default true
 * @returns {[string]}
 */
export const getPaymentMethodAmount = (formatAmount = true) => {
    const sessionStore = useSessionStore();
    const paymentStore = usePaymentsStore();
    const locale = sessionStore.language ?? "en-GB";
    const paymentMethodUsed = paymentStore.selectedProviderDetails;

    if (paymentMethodUsed !== null) {
        if (formatAmount) {
            const amount = formatAmountBasedOnLocaleAndCurrency(
                locale,
                paymentMethodUsed.localCurrencyCode,
                paymentMethodUsed.localAmount,
            );
            return amount;
        } else {
            return paymentMethodUsed.localAmount;
        }
    } else {
        return "";
    }
};

export const getPaymentMethodIcon = (paymentMethod: string) => {
    /**
     * Maps the payment method to an mdi-icon
     * @param {[string]} paymentMethod
     * @returns {[string]}
     */

    const iconMapping = {
        "Instant Payment": mdiLightningBolt,
        "Card ": mdiCreditCard,
        "Bank Transfer": mdiWeb,
    };

    return paymentMethod in iconMapping ? iconMapping[paymentMethod] : "";
};

export const getPaymentMethodIconColor = (paymentMethod: string) => {
    /**
     * Maps the payment method icon to a color.
     * @param {[string]} paymentMethod
     * @returns {[string]}
     */

    const iconMapping = {
        "Instant Payment": "warning",
        "Card ": "success",
        "Bank Transfer": "info",
    };

    return paymentMethod in iconMapping ? iconMapping[paymentMethod] : "";
};

/**
 * Makes a call to the BE API to retrieve additional order/transaction information.
 * If successful, it then makes a call to retrieve the list of available
 * payment methods. It waits for both of them to complete before redirecting
 * the user to the payment method page.
 * @param {[string]} orderId - order id
 * @param {[string]} countryCode
 * @param {[number]} amount
 */
const getPaymentInformation = async (
    orderId: string,
    countryCode: string,
    amount: number,
) => {
    try {
        const { error: apiError } =
            await getOrderAdditionalInformation(orderId);

        if (apiError == null) {
            const paymentMethods = await getPaymentMethodsList(
                countryCode,
                amount,
            );
            if (paymentMethods) {
                redirectToPaymentMethod(paymentMethods);
            }
        }
    } catch (error) {
        configSentryError(
            "OneTimeLink",
            "getPaymentInformation",
            `Error while calling the transaction information or payment methods API: ${error}`,
        );
    }
};

export const handleOneTimeLinkLogic = async (
    form: string,
    identifier: string,
    queryObject: any,
) => {
    /**
     * Parses and stores the link query params and calls the payment
     * methods api to continue the user journey
     * @param {[any]} queryObject
     * @returns {[void]}
     */
    const sessionStore = useSessionStore();
    const orgStore = useOrganisationStore();
    const checkoutStore = useCheckoutStore();
    const paymentStore = usePaymentsStore();

    /** Set the form name */
    if (form !== "null" && form !== "undefined") {
        orgStore.setFormName(form);
    } else {
        handleQueryParamsError(form, "form");
        return;
    }

    /** Set the payment id */
    if (form !== "null" && identifier !== "undefined") {
        if (identifier !== "x") {
            paymentStore.setPaymentId(identifier);
        } else {
            paymentStore.setPaymentId(null);
        }
    } else {
        handleQueryParamsError(identifier, "id");
        return;
    }

    const formattedQueryObject = keysToCamelCase(queryObject);

    /** Set order id if it exists */
    let orderId: string = "";

    if (hasValidProperty(formattedQueryObject, "orderId")) {
        orderId = formattedQueryObject.orderId as string;
        checkoutStore.setOrderId(orderId);
    }

    /** Save country code */
    if (hasValidProperty(formattedQueryObject, "country")) {
        const countryCode = formattedQueryObject.country as string;
        sessionStore.updateCountryCode(countryCode);
    } else {
        handleQueryParamsError(queryObject, "country");
        return;
    }

    /** Set amount */
    if (hasValidProperty(formattedQueryObject, "amount")) {
        const amount = parseFloat(formattedQueryObject.amount as string);
        checkoutStore.setPartnerLinkAmount(amount);
        /** Get the the additional user information and the payment method list and continue the user journey */
        paymentStore.setSelectedProvider(null);
        await getPaymentInformation(
            orderId,
            sessionStore.countryCode as string,
            amount,
        );
    } else {
        handleQueryParamsError(queryObject, "amount");
        return;
    }
};

export const getNoOfAvailablePaymentMethods = (): number => {
    /**
     * Gets the length of the payment method object and returns
     * the no of payment methods available
     * @returns {string}
     */
    const paymentStore = usePaymentsStore();

    return paymentStore.paymentMethods === null
        ? 0
        : paymentStore.paymentMethods.length;
};

export const getProviderName = (): string | null => {
    /**
     * Returns the selected provider name.
     * @returns { string | null }
     */
    const paymentStore = usePaymentsStore();
    return paymentStore.selectedProvider;
};

/**
 * Uses the selected payment method to return the currency code.
 * It defaults to GBP if no payment method is selected.
 * @returns {[string]}
 */
export const getPaymentMethodCurrencyCode = () => {
    const paymentStore = usePaymentsStore();
    const paymentMethodUsed = paymentStore.selectedProviderDetails;

    if (paymentMethodUsed !== null) {
        return paymentMethodUsed.currencyCode;
    } else {
        return "GBP";
    }
};

const getBankTransferNameAndIcon = (isFxMethod: boolean) => {
    /**
     * Maps the bank transfer provider to a name and icon.
     * @param {[boolean]} isFxMethod
     * @param {[string]} paymentCurrency
     * @param {[string | null]} schoolCurrency=null
     * @returns {[string]}
     */
    return {
        name: isFxMethod
            ? "default.internationalBankTransfer"
            : "default.bankTransfer",
        icon: isFxMethod ? mdiWeb : mdiBank,
    };
};

/**
 * Maps the provider to a name.
 *
 * @param {string} provider
 * @param {boolean} isFxMethod
 */
export const getPaymentMethodSelectionName = (
    provider: string,
    isFxMethod: boolean,
    supportedCardTypes?: SupportedCardType[],
): string => {
    const cardPaymentKey = !!supportedCardTypes
        ? areAllCardTypesPremium(supportedCardTypes)
            ? "default.premiumCardPayment"
            : "default.cardPayment"
        : "default.cardPayment";

    if (InstantPaymentProviders.includes(provider as PaymentMethods)) {
        return "default.payByBank";
    }

    if (CardPaymentProviders.includes(provider as PaymentMethods)) {
        if (supportedCardTypes?.length === 1) {
            return CardBrandNameMap[supportedCardTypes[0]] || cardPaymentKey;
        } else {
            return cardPaymentKey;
        }
    }

    if (BankTransferProviders.includes(provider as PaymentMethods)) {
        return getBankTransferNameAndIcon(isFxMethod).name;
    }

    return "default.unknownPaymentMethod";
};

export const getPaymentMethodSelectionIcon = (
    provider: string,
    isFxMethod: boolean,
): string => {
    /**
     * Maps the provider to an icon.
     * @param {[string]} provider
     * @param {[boolean]} isFxMethod
     * @returns {[string]}
     */
    if (InstantPaymentProviders.includes(provider as PaymentMethods)) {
        return mdiLightningBolt;
    }
    if (CardPaymentProviders.includes(provider as PaymentMethods)) {
        return mdiCreditCard;
    }
    if (BankTransferProviders.includes(provider as PaymentMethods)) {
        return getBankTransferNameAndIcon(isFxMethod).icon;
    }
    return mdiHelpCircle;
};

export const getPaymentMethodSelectionIconColor = (provider: string) => {
    /**
     * Maps the provider icon to a color.
     * @param {[string]} provider
     * @returns {[string]}
     */
    if (InstantPaymentProviders.includes(provider as PaymentMethods)) {
        return "warning";
    }
    if (CardPaymentProviders.includes(provider as PaymentMethods)) {
        return "success";
    }
    if (BankTransferProviders.includes(provider as PaymentMethods)) {
        return "info";
    }
    return "info";
};

export const showSepaPaymentInformation = (sepaCurrencies) => {
    /**
     * Computes whether SEPA payment information should be shown.
     * @returns {boolean} true if the current payment method currency is in the SEPA currencies list,
     * otherwise false.
     */
    if (!sepaCurrencies) return false;

    const currencyCode = getPaymentMethodCurrencyCode() as string;
    return sepaCurrencies.includes(currencyCode);
};

/**
 * Returns the localised list of supported card types used in alerts.
 * "-" if no supported card types are available.
 * Handles Amex formatting for premium cards.
 * @returns {string}
 */
export const getCardTypesListForAlert = (): string => {
    const paymentStore = usePaymentsStore();

    const cardTypesList =
        paymentStore.selectedProviderDetails?.supportedCardTypes ?? [];

    // map the card types to human-readable display names
    const mappedCardTypes = cardTypesList.flatMap((type) => {
        const formattedType = type.toLowerCase();
        if (formattedType.includes(SupportedCardType.VISA_MASTERCARD)) {
            return ["Mastercard", "Visa"];
        } else if (formattedType.includes(SupportedCardType.AMEX)) {
            return ["Amex"];
        } else if (formattedType === SupportedCardType.DINERS) {
            return ["Diners"];
        } else if (formattedType === SupportedCardType.DISCOVER) {
            return ["Discover"];
        } else if (formattedType === SupportedCardType.UNIONPAY) {
            return ["UnionPay"];
        } else if (formattedType === SupportedCardType.JCB) {
            return ["JCB"];
        } else if (formattedType === SupportedCardType.MAESTRO) {
            return ["Maestro"];
        } else {
            return [];
        }
    });

    // remove duplicates
    const uniqueCardTypes = Array.from(new Set(mappedCardTypes));

    // handle the case where no card types are available
    if (uniqueCardTypes.length === 0) {
        return "-";
    }

    return uniqueCardTypes.join("/");
};

/**
 * Maps the payment method to its type.
 * @param method The payment method to map.
 * @returns The corresponding PaymentMethodType.
 */
export const paymentMethodTypeMap = (
    method: PaymentMethods,
): PaymentMethodType => {
    if (BankTransferProviders.includes(method)) {
        return PaymentMethodType.GLOBAL;
    }
    if (InstantPaymentProviders.includes(method)) {
        return PaymentMethodType.INSTANT;
    }
    if (CardPaymentProviders.includes(method)) {
        return PaymentMethodType.CARD;
    }
    return PaymentMethodType.UNKNOWN;
};

/**
 * Checks whether every entry in `cardTypes` is a member of the
 * `SupportedPremiumCardType` enum (i.e. the array contains only
 * premium card types, with no standard/non-premium types mixed in).
 *
 * @param cardTypes - the card types to check
 * @returns `true` if all entries are premium card types, `false`
 * otherwise (including when `cardTypes` is nullish or empty)
 */
export const areAllCardTypesPremium = (
    cardTypes: SupportedCardType[],
): boolean => {
    if (!cardTypes?.length) {
        return false;
    }

    return cardTypes.every((type: SupportedCardType) =>
        Object.values(SupportedPremiumCardType).includes(
            type as unknown as SupportedPremiumCardType,
        ),
    );
};
